package diana.soleil.design.iraniancanadienbazzar.core.model

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/**
 * Author-provided multilingual content (business `description`/`serviceTitle`, product copy, …).
 * Resolve to a display string with [Localizer]; see `docs/LOCALIZATION.md`.
 *
 * For seed/back-compat the custom serializer accepts **either** a JSON object `{"en": …, "fr": …,
 * "fa": …}` **or** a bare string (treated as `{en: value}`), so existing single-language data decodes
 * without a migration. It always **writes** the object form.
 */
@Serializable(with = LocalizedTextSerializer::class)
data class LocalizedText(
    val en: String? = null,
    val fr: String? = null,
    val fa: String? = null,
) {
    /** True when no language has content. */
    val isEmpty: Boolean get() = en.isNullOrBlank() && fr.isNullOrBlank() && fa.isNullOrBlank()

    operator fun get(language: Language): String? = when (language) {
        Language.EN -> en
        Language.FR -> fr
        Language.FA -> fa
    }

    /**
     * Display string for [language] with graceful fallback:
     * selected → en → fr → fa → first non-blank → "". So a listing authored in any single language
     * still renders everywhere. See `docs/LOCALIZATION.md`.
     */
    fun resolve(language: Language): String =
        get(language)?.takeIf { it.isNotBlank() }
            ?: en?.takeIf { it.isNotBlank() }
            ?: fr?.takeIf { it.isNotBlank() }
            ?: fa?.takeIf { it.isNotBlank() }
            ?: ""

    companion object {
        fun of(value: String?): LocalizedText = LocalizedText(en = value)
    }
}

/**
 * Supported app/content languages. [code] matches BCP-47 and the `values-xx/` resource qualifier.
 * [endonym] is the language's own name (shown the same regardless of UI locale — conventionally not
 * translated), used by the language switcher.
 */
@Serializable
enum class Language(val code: String, val isRtl: Boolean, val endonym: String) {
    EN("en", false, "English"),
    FR("fr", false, "Français"),
    FA("fa", true, "فارسی");

    companion object {
        val Default: Language = EN
        fun fromCode(code: String?): Language =
            entries.firstOrNull { it.code.equals(code, ignoreCase = true) } ?: Default
    }
}

internal object LocalizedTextSerializer : KSerializer<LocalizedText> {
    override val descriptor: SerialDescriptor =
        buildClassSerialDescriptor("LocalizedText") {
            // Element order documents the shape; decoding is driven by the JSON tree below.
        }

    override fun deserialize(decoder: Decoder): LocalizedText {
        val jsonDecoder = decoder as? JsonDecoder
            ?: error("LocalizedText can only be decoded from JSON-compatible formats")
        return when (val element = jsonDecoder.decodeJsonElement()) {
            is JsonPrimitive -> LocalizedText.of(element.contentOrNull)
            is JsonObject -> LocalizedText(
                en = element["en"]?.jsonPrimitive?.contentOrNull,
                fr = element["fr"]?.jsonPrimitive?.contentOrNull,
                fa = element["fa"]?.jsonPrimitive?.contentOrNull,
            )
            else -> LocalizedText()
        }
    }

    override fun serialize(encoder: Encoder, value: LocalizedText) {
        val jsonEncoder = encoder as? kotlinx.serialization.json.JsonEncoder
            ?: error("LocalizedText can only be encoded to JSON-compatible formats")
        val obj = buildMap {
            value.en?.let { put("en", JsonPrimitive(it)) }
            value.fr?.let { put("fr", JsonPrimitive(it)) }
            value.fa?.let { put("fa", JsonPrimitive(it)) }
        }
        jsonEncoder.encodeJsonElement(JsonObject(obj))
    }
}
